/**
 * Per-page SEO helper. Mutates document.head directly so meta tags update
 * during SPA navigation. The static HTML <head> is also stamped with the
 * correct values at build time by scripts/seo-postbuild.js — this component
 * exists for SPA navigation parity, not for first-load SEO.
 *
 * Defensively de-duplicates head tags before updating. Necessary because
 * Emergent's static host can serve the root /index.html (with its homepage
 * default meta tags) as a fallback for unknown routes — when the React app
 * then mounts and runs <Seo>, we'd end up with two of every meta tag in the
 * cached crawler response. Removing duplicates first keeps Google's
 * "Duplicate without user-selected canonical" warning from firing.
 */
import { useEffect } from "react";

const dedupeBy = (selector) => {
  const els = document.head.querySelectorAll(selector);
  for (let i = 1; i < els.length; i += 1) els[i].remove();
};

const dedupeAll = () => {
  dedupeBy("title");
  dedupeBy('meta[name="description"]');
  dedupeBy('link[rel="canonical"]');
  dedupeBy('meta[property="og:title"]');
  dedupeBy('meta[property="og:description"]');
  dedupeBy('meta[property="og:url"]');
  dedupeBy('meta[property="og:image"]');
  dedupeBy('meta[property="og:image:secure_url"]');
  dedupeBy('meta[name="twitter:title"]');
  dedupeBy('meta[name="twitter:description"]');
  dedupeBy('meta[name="twitter:image"]');
};

const setMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const PAGE_JSONLD_ID = "page-jsonld";

const setJsonLd = (data) => {
  let el = document.getElementById(PAGE_JSONLD_ID);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = PAGE_JSONLD_ID;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export function Seo({ title, description, canonical, image, jsonLd }) {
  // setMeta, setLink and setJsonLd are module-scope helpers (not React state).
  // The eslint-plugin-react-hooks/exhaustive-deps rule false-positives on these.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dedupeAll();
    if (title) document.title = title;
    setMeta("name", "description", description);
    setLink("canonical", canonical);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("property", "og:image:secure_url", image);
    }
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);
    setJsonLd(jsonLd);
  }, [title, description, canonical, image, jsonLd]);
  return null;
}
